/**
 * Fragment shader que aplica una transición entre dos texturas de vídeo.
 *
 * Acepta `u_transition` (id numérico, coincide con FFmpeg xfade) y
 * `u_progress` (0..1). Cubre las ~35 transiciones más usadas. Las no
 * implementadas hacen fallback a fundido (fade), pero el servidor SÍ las
 * exporta con la transición correcta — el preview pierde fidelidad sólo
 * en ese 1% de casos exóticos.
 */

export const TRANSITION_VERT = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  v_uv.y = 1.0 - v_uv.y;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

export const TRANSITION_FRAG = `#version 300 es
precision highp float;

uniform sampler2D u_videoA;
uniform sampler2D u_videoB;
uniform int       u_transition;
uniform float     u_progress;        // 0..1

in vec2 v_uv;
out vec4 fragColor;

// Helpers --------------------------------------------------------------
vec4 sampA(vec2 uv) { return texture(u_videoA, uv); }
vec4 sampB(vec2 uv) { return texture(u_videoB, uv); }

float rand(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898,78.233))) * 43758.5453);
}

float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

float ease(float t) {
  return t * t * (3.0 - 2.0 * t);
}

// Implementaciones -----------------------------------------------------
vec4 fade(vec2 uv, float p)        { return mix(sampA(uv), sampB(uv), p); }
vec4 fadeblack(vec2 uv, float p)   {
  vec3 k = vec3(0.0);
  if (p < 0.5) return vec4(mix(sampA(uv).rgb, k, p * 2.0), 1.0);
  return vec4(mix(k, sampB(uv).rgb, (p - 0.5) * 2.0), 1.0);
}
vec4 fadewhite(vec2 uv, float p)   {
  vec3 k = vec3(1.0);
  if (p < 0.5) return vec4(mix(sampA(uv).rgb, k, p * 2.0), 1.0);
  return vec4(mix(k, sampB(uv).rgb, (p - 0.5) * 2.0), 1.0);
}
vec4 fadegrays(vec2 uv, float p)   {
  vec3 a = sampA(uv).rgb;
  vec3 b = sampB(uv).rgb;
  vec3 ga = vec3(luma(a));
  vec3 gb = vec3(luma(b));
  if (p < 0.5) return vec4(mix(a, ga, p * 2.0), 1.0);
  return vec4(mix(gb, b, (p - 0.5) * 2.0), 1.0);
}
vec4 fadefast(vec2 uv, float p)    { return fade(uv, clamp(p * 1.6, 0.0, 1.0)); }
vec4 fadeslow(vec2 uv, float p)    { return fade(uv, ease(p)); }

vec4 wipeleft(vec2 uv, float p)    { return uv.x < (1.0 - p) ? sampA(uv) : sampB(uv); }
vec4 wiperight(vec2 uv, float p)   { return uv.x > p ? sampA(uv) : sampB(uv); }
vec4 wipeup(vec2 uv, float p)      { return uv.y > p ? sampA(uv) : sampB(uv); }
vec4 wipedown(vec2 uv, float p)    { return uv.y < (1.0 - p) ? sampA(uv) : sampB(uv); }

vec4 wipeDiag(vec2 uv, float p, vec2 origin) {
  float d = distance(uv, origin) / 1.4142;
  return d < p ? sampB(uv) : sampA(uv);
}
vec4 wipetl(vec2 uv, float p) { return wipeDiag(uv, p, vec2(0.0, 0.0)); }
vec4 wipetr(vec2 uv, float p) { return wipeDiag(uv, p, vec2(1.0, 0.0)); }
vec4 wipebl(vec2 uv, float p) { return wipeDiag(uv, p, vec2(0.0, 1.0)); }
vec4 wipebr(vec2 uv, float p) { return wipeDiag(uv, p, vec2(1.0, 1.0)); }

vec4 slide(vec2 uv, float p, vec2 dir) {
  // dir es el sentido del desplazamiento del clip B entrando
  vec2 uvB = uv - dir * (1.0 - p);
  vec2 uvA = uv + dir * p;
  bool inB = all(greaterThanEqual(uvB, vec2(0.0))) && all(lessThanEqual(uvB, vec2(1.0)));
  return inB ? sampB(uvB) : sampA(uvA);
}
vec4 slideleft(vec2 uv, float p)  { return slide(uv, p, vec2(-1.0,  0.0)); }
vec4 slideright(vec2 uv, float p) { return slide(uv, p, vec2( 1.0,  0.0)); }
vec4 slideup(vec2 uv, float p)    { return slide(uv, p, vec2( 0.0, -1.0)); }
vec4 slidedown(vec2 uv, float p)  { return slide(uv, p, vec2( 0.0,  1.0)); }

vec4 smoothleft(vec2 uv, float p)  { return slide(uv, ease(p), vec2(-1.0,  0.0)); }
vec4 smoothright(vec2 uv, float p) { return slide(uv, ease(p), vec2( 1.0,  0.0)); }
vec4 smoothup(vec2 uv, float p)    { return slide(uv, ease(p), vec2( 0.0, -1.0)); }
vec4 smoothdown(vec2 uv, float p)  { return slide(uv, ease(p), vec2( 0.0,  1.0)); }

// Cover: B entra por encima de A
vec4 cover(vec2 uv, float p, vec2 dir) {
  vec2 uvB = uv - dir * (1.0 - p);
  bool inB = all(greaterThanEqual(uvB, vec2(0.0))) && all(lessThanEqual(uvB, vec2(1.0)));
  return inB ? sampB(uvB) : sampA(uv);
}
vec4 coverleft(vec2 uv, float p)  { return cover(uv, p, vec2(-1.0,  0.0)); }
vec4 coverright(vec2 uv, float p) { return cover(uv, p, vec2( 1.0,  0.0)); }
vec4 coverup(vec2 uv, float p)    { return cover(uv, p, vec2( 0.0, -1.0)); }
vec4 coverdown(vec2 uv, float p)  { return cover(uv, p, vec2( 0.0,  1.0)); }

// Reveal: A se aparta y revela B abajo
vec4 reveal(vec2 uv, float p, vec2 dir) {
  vec2 uvA = uv + dir * p;
  bool inA = all(greaterThanEqual(uvA, vec2(0.0))) && all(lessThanEqual(uvA, vec2(1.0)));
  return inA ? sampA(uvA) : sampB(uv);
}
vec4 revealleft(vec2 uv, float p)  { return reveal(uv, p, vec2(-1.0,  0.0)); }
vec4 revealright(vec2 uv, float p) { return reveal(uv, p, vec2( 1.0,  0.0)); }
vec4 revealup(vec2 uv, float p)    { return reveal(uv, p, vec2( 0.0, -1.0)); }
vec4 revealdown(vec2 uv, float p)  { return reveal(uv, p, vec2( 0.0,  1.0)); }

vec4 circleopen(vec2 uv, float p) {
  float d = distance(uv, vec2(0.5));
  float r = p * 0.7;
  return d < r ? sampB(uv) : sampA(uv);
}
vec4 circleclose(vec2 uv, float p) {
  float d = distance(uv, vec2(0.5));
  float r = (1.0 - p) * 0.7;
  return d > r ? sampB(uv) : sampA(uv);
}
vec4 circlecrop(vec2 uv, float p) {
  vec2 c = vec2(0.5);
  float d = distance(uv, c);
  float r = abs(0.5 - p) * 0.85;
  vec4 base = vec4(0.0,0.0,0.0,1.0);
  if (p < 0.5) return d > r ? sampA(uv) : base;
  return d > r ? sampB(uv) : base;
}
vec4 rectcrop(vec2 uv, float p) {
  vec2 d = abs(uv - 0.5);
  float r = abs(0.5 - p) * 0.85;
  vec4 base = vec4(0.0,0.0,0.0,1.0);
  bool out_ = max(d.x, d.y) > r;
  return out_ ? (p < 0.5 ? sampA(uv) : sampB(uv)) : base;
}

vec4 vertopen(vec2 uv, float p) {
  float band = 0.5 - p * 0.5;
  return abs(uv.x - 0.5) > band ? sampB(uv) : sampA(uv);
}
vec4 vertclose(vec2 uv, float p) {
  float band = p * 0.5;
  return abs(uv.x - 0.5) < band ? sampB(uv) : sampA(uv);
}
vec4 horzopen(vec2 uv, float p) {
  float band = 0.5 - p * 0.5;
  return abs(uv.y - 0.5) > band ? sampB(uv) : sampA(uv);
}
vec4 horzclose(vec2 uv, float p) {
  float band = p * 0.5;
  return abs(uv.y - 0.5) < band ? sampB(uv) : sampA(uv);
}

vec4 dissolve(vec2 uv, float p) {
  float n = rand(uv * 100.0);
  return n < p ? sampB(uv) : sampA(uv);
}

vec4 pixelize(vec2 uv, float p) {
  float blocks = mix(40.0, 4.0, sin(p * 3.14159));
  vec2 g = floor(uv * blocks) / blocks + 0.5 / blocks;
  return mix(sampA(g), sampB(g), p);
}

vec4 squeezeh(vec2 uv, float p) {
  float s = 1.0 - p;
  vec2 a = vec2(0.5 + (uv.x - 0.5) / max(s, 0.01), uv.y);
  vec2 b = vec2(0.5 + (uv.x - 0.5) / max(p, 0.01), uv.y);
  if (p < 0.5) {
    return all(greaterThanEqual(a, vec2(0.0))) && all(lessThanEqual(a, vec2(1.0)))
      ? sampA(a) : vec4(0,0,0,1);
  }
  return all(greaterThanEqual(b, vec2(0.0))) && all(lessThanEqual(b, vec2(1.0)))
    ? sampB(b) : vec4(0,0,0,1);
}
vec4 squeezev(vec2 uv, float p) {
  float s = 1.0 - p;
  vec2 a = vec2(uv.x, 0.5 + (uv.y - 0.5) / max(s, 0.01));
  vec2 b = vec2(uv.x, 0.5 + (uv.y - 0.5) / max(p, 0.01));
  if (p < 0.5) {
    return all(greaterThanEqual(a, vec2(0.0))) && all(lessThanEqual(a, vec2(1.0)))
      ? sampA(a) : vec4(0,0,0,1);
  }
  return all(greaterThanEqual(b, vec2(0.0))) && all(lessThanEqual(b, vec2(1.0)))
    ? sampB(b) : vec4(0,0,0,1);
}
vec4 zoomin(vec2 uv, float p) {
  // A se aleja, B aparece
  vec2 z = (uv - 0.5) / (1.0 + p * 2.0) + 0.5;
  return mix(sampA(z), sampB(uv), p);
}

vec4 radial(vec2 uv, float p) {
  vec2 d = uv - 0.5;
  float a = atan(d.y, d.x);
  float ang = (a + 3.14159) / 6.2832;  // 0..1
  return ang < p ? sampB(uv) : sampA(uv);
}
vec4 distanceT(vec2 uv, float p) {
  vec4 a = sampA(uv);
  vec4 b = sampB(uv);
  float la = luma(a.rgb);
  return la < p ? b : a;
}

vec4 hblur(vec2 uv, float p) {
  float s = sin(p * 3.14159) * 0.04;
  vec4 acc = vec4(0.0);
  for (int i = -4; i <= 4; i++) {
    acc += mix(sampA(uv + vec2(float(i) * s * 0.25, 0.0)),
               sampB(uv + vec2(float(i) * s * 0.25, 0.0)), p);
  }
  return acc / 9.0;
}

vec4 diag(vec2 uv, float p, vec2 dir) {
  float d = dot(uv, dir) / dot(vec2(1.0), abs(dir));
  return d < p ? sampB(uv) : sampA(uv);
}
vec4 diagtl(vec2 uv, float p) { return diag(uv, p, vec2(-1.0, -1.0)); }
vec4 diagtr(vec2 uv, float p) { return diag(uv, p, vec2( 1.0, -1.0)); }
vec4 diagbl(vec2 uv, float p) { return diag(uv, p, vec2(-1.0,  1.0)); }
vec4 diagbr(vec2 uv, float p) { return diag(uv, p, vec2( 1.0,  1.0)); }

// Slice: tiras horizontales/verticales alternantes
vec4 slice(vec2 uv, float p, bool horizontal, float dirSign) {
  float band = horizontal ? uv.y : uv.x;
  float slices = 10.0;
  float idx = floor(band * slices);
  float offset = mod(idx, 2.0) == 0.0 ? p : 1.0 - p;
  vec2 sh = horizontal ? vec2(offset * dirSign, 0.0) : vec2(0.0, offset * dirSign);
  vec2 uvB = uv - sh;
  return all(greaterThanEqual(uvB, vec2(0.0))) && all(lessThanEqual(uvB, vec2(1.0)))
    ? mix(sampA(uv), sampB(uvB), p)
    : sampA(uv);
}
vec4 hlslice(vec2 uv, float p) { return slice(uv, p, true, -1.0); }
vec4 hrslice(vec2 uv, float p) { return slice(uv, p, true,  1.0); }
vec4 vuslice(vec2 uv, float p) { return slice(uv, p, false, -1.0); }
vec4 vdslice(vec2 uv, float p) { return slice(uv, p, false,  1.0); }

vec4 wind(vec2 uv, float p, vec2 dir) {
  float n = rand(vec2(dot(uv, dir.yx) * 20.0, 0.0));
  float t = p + n * 0.15;
  return dot(uv, dir) < t ? sampB(uv) : sampA(uv);
}
vec4 hlwind(vec2 uv, float p) { return wind(uv, p, vec2(-1.0, 0.0)); }
vec4 hrwind(vec2 uv, float p) { return wind(uv, p, vec2( 1.0, 0.0)); }
vec4 vuwind(vec2 uv, float p) { return wind(uv, p, vec2(0.0, -1.0)); }
vec4 vdwind(vec2 uv, float p) { return wind(uv, p, vec2(0.0,  1.0)); }

// gl-transitions extras ------------------------------------------------
vec4 burn(vec2 uv, float p) {
  vec4 a = sampA(uv);
  vec4 b = sampB(uv);
  vec3 fire = vec3(0.9, 0.4, 0.05) * (1.0 - p);
  float n = rand(uv * (50.0 + sin(p*6.28) * 100.0));
  float burnEdge = smoothstep(p - 0.1, p + 0.1, n);
  return mix(b, mix(a, vec4(fire, 1.0), 1.0 - burnEdge), 1.0 - p);
}
vec4 glitchT(vec2 uv, float p) {
  float jitter = (rand(vec2(floor(uv.y * 30.0), p)) - 0.5) * 0.2 * (1.0 - abs(p - 0.5) * 2.0);
  vec2 uvA = uv + vec2(jitter, 0.0);
  vec2 uvB = uv - vec2(jitter, 0.0);
  vec3 a = sampA(uvA).rgb;
  vec3 b = sampB(uvB).rgb;
  vec3 c = mix(a, b, p);
  // chromatic split
  c.r = mix(a.r, b.r, clamp(p - 0.05, 0.0, 1.0));
  c.b = mix(a.b, b.b, clamp(p + 0.05, 0.0, 1.0));
  return vec4(c, 1.0);
}
vec4 swirl(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float r = length(c);
  float ang = atan(c.y, c.x) + sin(p * 6.283) * (1.0 - r * 2.0) * 2.0;
  vec2 q = vec2(cos(ang), sin(ang)) * r + 0.5;
  return mix(sampA(q), sampB(q), p);
}
vec4 ripple(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float r = length(c);
  float wave = sin(r * 40.0 - p * 18.0) * 0.04 * (1.0 - p);
  vec2 q = uv + normalize(c) * wave;
  return mix(sampA(q), sampB(uv), p);
}
vec4 doorway(vec2 uv, float p) {
  float half_ = 0.5 - p * 0.5;
  if (uv.x < half_ || uv.x > 1.0 - half_) return sampB(uv);
  vec2 q = vec2((uv.x - half_) / max(2.0 * (0.5 - half_), 0.01), uv.y);
  return sampA(q);
}
vec4 kaleidoscope(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float ang = atan(c.y, c.x);
  float r = length(c);
  float seg = 6.283 / 6.0;
  ang = mod(ang, seg);
  ang = abs(ang - seg * 0.5);
  vec2 q = vec2(cos(ang), sin(ang)) * r + 0.5;
  return mix(sampA(q), sampB(uv), p);
}
vec4 morph(vec2 uv, float p) {
  vec4 a = sampA(uv);
  vec4 b = sampB(uv);
  vec2 disp = (a.rg - b.rg) * 0.1 * sin(p * 3.14);
  return mix(sampA(uv + disp), sampB(uv - disp), p);
}
vec4 pageCurl(vec2 uv, float p) {
  // efecto simplificado: borde diagonal con sombra
  float d = uv.x + uv.y;
  if (d < p * 2.0) return sampB(uv);
  float edge = smoothstep(p * 2.0, p * 2.0 + 0.05, d);
  vec4 a = sampA(uv);
  // sombra cerca del pliegue
  float shadow = smoothstep(p * 2.0, p * 2.0 + 0.15, d);
  return vec4(a.rgb * mix(0.6, 1.0, shadow), 1.0);
}

// ----- Patrones --------------------------------------------------------
vec4 crosshatch(vec2 uv, float p) {
  float hatch = sin(uv.x * 100.0) + sin(uv.y * 100.0);
  float n = (hatch + 2.0) * 0.25;
  return n < p ? sampB(uv) : sampA(uv);
}
vec4 mosaic(vec2 uv, float p) {
  float blocks = 14.0;
  vec2 block = floor(uv * blocks);
  float r = rand(block);
  return r < p ? sampB(uv) : sampA(uv);
}
vec4 polkadot(vec2 uv, float p) {
  float n = 14.0;
  vec2 g = fract(uv * n) - 0.5;
  float d = length(g) * 1.5;
  return d < p ? sampB(uv) : sampA(uv);
}
vec4 hexagons(vec2 uv, float p) {
  vec2 s = vec2(1.0, 1.7320508);
  vec2 h = 0.5 * s;
  vec2 c1 = mod(uv * 14.0, s) - h;
  vec2 c2 = mod(uv * 14.0 + h, s) - h;
  vec2 g = dot(c1,c1) < dot(c2,c2) ? c1 : c2;
  float d = length(g) / 0.6;
  return d < p ? sampB(uv) : sampA(uv);
}
vec4 diamonds(vec2 uv, float p) {
  vec2 g = abs(fract(uv * 12.0) - 0.5);
  float d = (g.x + g.y);
  return d < p ? sampB(uv) : sampA(uv);
}
vec4 scanlines(vec2 uv, float p) {
  float scan = step(0.5, fract(uv.y * 80.0));
  vec4 c = mix(sampA(uv), sampB(uv), p);
  c.rgb *= 0.75 + 0.25 * scan;
  return c;
}

// ----- Formas ----------------------------------------------------------
vec4 squareWipe(vec2 uv, float p) {
  vec2 d = abs(uv - 0.5);
  float r = max(d.x, d.y) * 2.0;
  return r < p * 1.2 ? sampB(uv) : sampA(uv);
}
vec4 starWipe(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float ang = atan(c.y, c.x);
  float r = length(c);
  float starR = 0.15 + 0.06 * cos(ang * 5.0);
  return r < p * starR * 4.0 ? sampB(uv) : sampA(uv);
}
vec4 plusWipe(vec2 uv, float p) {
  vec2 d = abs(uv - 0.5);
  float r = min(d.x, d.y) * 2.0;
  return r < p * 0.5 ? sampB(uv) : sampA(uv);
}
vec4 heartShape(vec2 uv, float p) {
  vec2 q = (uv - 0.5) * 2.0;
  q.x *= 1.2;
  q.y = -q.y + 0.2;
  float h = pow(q.x*q.x + q.y*q.y - 0.3, 3.0) - q.x*q.x * q.y*q.y * q.y;
  float r = -0.4 + p * 0.9;
  return h < r ? sampB(uv) : sampA(uv);
}
vec4 letterbox(vec2 uv, float p) {
  float band = 0.5 * p;
  bool inBlack = uv.y < band || uv.y > 1.0 - band;
  vec4 c = mix(sampA(uv), sampB(uv), p);
  return inBlack ? vec4(0.0, 0.0, 0.0, 1.0) : c;
}
vec4 circleWaves(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float r = length(c);
  float wave = sin(r * 24.0 - p * 18.0) * 0.5 + 0.5;
  float mask = step(r, p * 1.4);
  vec4 col = mix(sampA(uv), sampB(uv), mask);
  col.rgb *= 0.7 + 0.3 * wave;
  return col;
}

// ----- Blur / Distorsión ----------------------------------------------
vec4 dreamyZoom(vec2 uv, float p) {
  vec2 zoomA = (uv - 0.5) * (1.0 + p * 0.6) + 0.5;
  vec2 zoomB = (uv - 0.5) * (1.5 - p * 0.5) + 0.5;
  return mix(sampA(zoomA), sampB(zoomB), p);
}
vec4 linearBlur(vec2 uv, float p) {
  vec4 acc = vec4(0.0);
  float s = sin(p * 3.14159) * 0.025;
  for (int i = -3; i <= 3; i++) {
    vec2 o = vec2(float(i) * s * 0.5, 0.0);
    acc += mix(sampA(uv + o), sampB(uv + o), p);
  }
  return acc / 7.0;
}
vec4 zoomBlurT(vec2 uv, float p) {
  vec4 acc = vec4(0.0);
  vec2 c = uv - 0.5;
  for (int i = 0; i < 8; i++) {
    float k = float(i) / 8.0 * sin(p * 3.14159) * 0.15;
    acc += mix(sampA(uv + c * k), sampB(uv + c * k), p);
  }
  return acc / 8.0;
}
vec4 stretchBlur(vec2 uv, float p) {
  vec4 acc = vec4(0.0);
  float s = sin(p * 3.14159) * 0.10;
  for (int i = -4; i <= 4; i++) {
    vec2 o = vec2(0.0, float(i) * s * 0.25);
    acc += mix(sampA(uv + o), sampB(uv + o), p);
  }
  return acc / 9.0;
}
vec4 waterdrop(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float r = length(c);
  float wave = sin(r * 35.0 - p * 18.0) * 0.05;
  vec2 q = uv + normalize(c) * wave * (1.0 - p);
  return mix(sampA(q), sampB(uv), smoothstep(p - 0.15, p + 0.05, r * 1.5));
}
vec4 noiseScramble(vec2 uv, float p) {
  float intensity = sin(p * 3.14159) * 0.08;
  vec2 off = vec2(rand(uv * 100.0) - 0.5, rand(uv * 100.0 + 1.0) - 0.5) * intensity;
  return mix(sampA(uv + off), sampB(uv + off), p);
}
vec4 rotateScale(vec2 uv, float p) {
  vec2 c = uv - 0.5;
  float ang = p * 3.14159;
  float s = sin(ang), co = cos(ang);
  mat2 rotA = mat2(co, -s, s, co);
  vec2 a = rotA * c / (1.0 + p * 0.8) + 0.5;
  vec2 b = -rotA * c / max(0.001, 1.5 - p * 0.5) + 0.5;
  return mix(sampA(a), sampB(b), p);
}

// ----- Persianas / Grid -----------------------------------------------
vec4 windowblinds(vec2 uv, float p) {
  float lines = 14.0;
  float strip = fract(uv.y * lines);
  return strip < p ? sampB(uv) : sampA(uv);
}
vec4 windowblindsV(vec2 uv, float p) {
  float lines = 14.0;
  float strip = fract(uv.x * lines);
  return strip < p ? sampB(uv) : sampA(uv);
}
vec4 shutterhorz(vec2 uv, float p) {
  float band = abs(uv.y - 0.5);
  return band > 0.5 - p * 0.5 ? sampB(uv) : sampA(uv);
}
vec4 shuttervert(vec2 uv, float p) {
  float band = abs(uv.x - 0.5);
  return band > 0.5 - p * 0.5 ? sampB(uv) : sampA(uv);
}
vec4 gridflip(vec2 uv, float p) {
  vec2 cell = floor(uv * 8.0);
  float delay = rand(cell) * 0.6;
  float local = clamp((p - delay) / 0.4, 0.0, 1.0);
  return local > 0.5 ? sampB(uv) : sampA(uv);
}

// ----- Glitch / FX TV --------------------------------------------------
vec4 filmburnPro(vec2 uv, float p) {
  vec3 fire = vec3(1.0, 0.5, 0.0);
  vec3 dark = vec3(0.1, 0.05, 0.0);
  float n1 = rand(uv * (30.0 + sin(p*10.0)*20.0));
  float n2 = rand(uv * 80.0 + p * 5.0);
  float edge = smoothstep(p - 0.15, p + 0.05, n1);
  float ash = smoothstep(p - 0.05, p + 0.10, n2);
  vec3 a = sampA(uv).rgb;
  vec3 b = sampB(uv).rgb;
  vec3 burnt = mix(dark, fire, ash * (1.0 - p));
  vec3 col = mix(burnt, mix(a, b, p), edge);
  return vec4(col, 1.0);
}
vec4 glitchHard(vec2 uv, float p) {
  float t = floor(p * 10.0) / 10.0;
  float lineJ = (rand(vec2(floor(uv.y * 60.0), t)) - 0.5) * 0.3;
  float strength = sin(p * 3.14) * 1.2;
  vec2 jUv = uv + vec2(lineJ * strength, 0.0);
  vec3 a = sampA(jUv).rgb;
  vec3 b = sampB(jUv).rgb;
  vec3 c = mix(a, b, p);
  c.r = mix(a.r, b.r, clamp(p - 0.18, 0.0, 1.0));
  c.g = mix(a.g, b.g, p);
  c.b = mix(a.b, b.b, clamp(p + 0.18, 0.0, 1.0));
  return vec4(c, 1.0);
}
vec4 invertFlash(vec2 uv, float p) {
  vec4 c = mix(sampA(uv), sampB(uv), p);
  float flash = smoothstep(0.43, 0.5, p) * (1.0 - smoothstep(0.5, 0.57, p));
  c.rgb = mix(c.rgb, 1.0 - c.rgb, flash);
  return c;
}
vec4 rgbSplitT(vec2 uv, float p) {
  float s = sin(p * 3.14159) * 0.04;
  vec4 c = mix(sampA(uv), sampB(uv), p);
  float r = mix(sampA(uv + vec2(s,0)).r, sampB(uv + vec2(s,0)).r, p);
  float b = mix(sampA(uv - vec2(s,0)).b, sampB(uv - vec2(s,0)).b, p);
  return vec4(r, c.g, b, 1.0);
}

void main() {
  vec4 c;
  int tr = u_transition;
  float p = clamp(u_progress, 0.0, 1.0);

  if      (tr == 1)  c = wipeleft(v_uv, p);
  else if (tr == 2)  c = wiperight(v_uv, p);
  else if (tr == 3)  c = wipeup(v_uv, p);
  else if (tr == 4)  c = wipedown(v_uv, p);
  else if (tr == 5)  c = slideleft(v_uv, p);
  else if (tr == 6)  c = slideright(v_uv, p);
  else if (tr == 7)  c = slideup(v_uv, p);
  else if (tr == 8)  c = slidedown(v_uv, p);
  else if (tr == 9)  c = circlecrop(v_uv, p);
  else if (tr == 10) c = rectcrop(v_uv, p);
  else if (tr == 11) c = distanceT(v_uv, p);
  else if (tr == 12) c = fadeblack(v_uv, p);
  else if (tr == 13) c = fadewhite(v_uv, p);
  else if (tr == 14) c = radial(v_uv, p);
  else if (tr == 15) c = smoothleft(v_uv, p);
  else if (tr == 16) c = smoothright(v_uv, p);
  else if (tr == 17) c = smoothup(v_uv, p);
  else if (tr == 18) c = smoothdown(v_uv, p);
  else if (tr == 19) c = circleopen(v_uv, p);
  else if (tr == 20) c = circleclose(v_uv, p);
  else if (tr == 21) c = vertopen(v_uv, p);
  else if (tr == 22) c = vertclose(v_uv, p);
  else if (tr == 23) c = horzopen(v_uv, p);
  else if (tr == 24) c = horzclose(v_uv, p);
  else if (tr == 25) c = dissolve(v_uv, p);
  else if (tr == 26) c = pixelize(v_uv, p);
  else if (tr == 27) c = diagtl(v_uv, p);
  else if (tr == 28) c = diagtr(v_uv, p);
  else if (tr == 29) c = diagbl(v_uv, p);
  else if (tr == 30) c = diagbr(v_uv, p);
  else if (tr == 31) c = hlslice(v_uv, p);
  else if (tr == 32) c = hrslice(v_uv, p);
  else if (tr == 33) c = vuslice(v_uv, p);
  else if (tr == 34) c = vdslice(v_uv, p);
  else if (tr == 35) c = hblur(v_uv, p);
  else if (tr == 36) c = fadegrays(v_uv, p);
  else if (tr == 37) c = wipetl(v_uv, p);
  else if (tr == 38) c = wipetr(v_uv, p);
  else if (tr == 39) c = wipebl(v_uv, p);
  else if (tr == 40) c = wipebr(v_uv, p);
  else if (tr == 41) c = squeezeh(v_uv, p);
  else if (tr == 42) c = squeezev(v_uv, p);
  else if (tr == 43) c = zoomin(v_uv, p);
  else if (tr == 44) c = fadefast(v_uv, p);
  else if (tr == 45) c = fadeslow(v_uv, p);
  else if (tr == 46) c = hlwind(v_uv, p);
  else if (tr == 47) c = hrwind(v_uv, p);
  else if (tr == 48) c = vuwind(v_uv, p);
  else if (tr == 49) c = vdwind(v_uv, p);
  else if (tr == 50) c = coverleft(v_uv, p);
  else if (tr == 51) c = coverright(v_uv, p);
  else if (tr == 52) c = coverup(v_uv, p);
  else if (tr == 53) c = coverdown(v_uv, p);
  else if (tr == 54) c = revealleft(v_uv, p);
  else if (tr == 55) c = revealright(v_uv, p);
  else if (tr == 56) c = revealup(v_uv, p);
  else if (tr == 57) c = revealdown(v_uv, p);
  else if (tr == 100) c = burn(v_uv, p);
  else if (tr == 101) c = glitchT(v_uv, p);
  else if (tr == 102) c = morph(v_uv, p);
  else if (tr == 103) c = swirl(v_uv, p);
  else if (tr == 104) c = ripple(v_uv, p);
  else if (tr == 105) c = doorway(v_uv, p);
  else if (tr == 106) c = kaleidoscope(v_uv, p);
  else if (tr == 107) c = pageCurl(v_uv, p);
  // Patrones
  else if (tr == 108) c = crosshatch(v_uv, p);
  else if (tr == 109) c = mosaic(v_uv, p);
  else if (tr == 110) c = polkadot(v_uv, p);
  else if (tr == 111) c = hexagons(v_uv, p);
  else if (tr == 112) c = diamonds(v_uv, p);
  else if (tr == 113) c = scanlines(v_uv, p);
  // Formas
  else if (tr == 114) c = squareWipe(v_uv, p);
  else if (tr == 115) c = starWipe(v_uv, p);
  else if (tr == 116) c = plusWipe(v_uv, p);
  else if (tr == 117) c = heartShape(v_uv, p);
  else if (tr == 118) c = letterbox(v_uv, p);
  else if (tr == 119) c = circleWaves(v_uv, p);
  // Blur / Distorsión
  else if (tr == 120) c = dreamyZoom(v_uv, p);
  else if (tr == 121) c = linearBlur(v_uv, p);
  else if (tr == 122) c = zoomBlurT(v_uv, p);
  else if (tr == 123) c = stretchBlur(v_uv, p);
  else if (tr == 124) c = waterdrop(v_uv, p);
  else if (tr == 125) c = noiseScramble(v_uv, p);
  else if (tr == 126) c = rotateScale(v_uv, p);
  // Persianas / Grid
  else if (tr == 127) c = windowblinds(v_uv, p);
  else if (tr == 128) c = windowblindsV(v_uv, p);
  else if (tr == 129) c = shutterhorz(v_uv, p);
  else if (tr == 130) c = shuttervert(v_uv, p);
  else if (tr == 131) c = gridflip(v_uv, p);
  // Glitch / FX TV
  else if (tr == 132) c = filmburnPro(v_uv, p);
  else if (tr == 133) c = glitchHard(v_uv, p);
  else if (tr == 134) c = invertFlash(v_uv, p);
  else if (tr == 135) c = rgbSplitT(v_uv, p);
  else               c = fade(v_uv, p);

  fragColor = vec4(c.rgb, 1.0);
}
`;
